import type { IdentityProviderOut } from "../shared/types";
import type { CreatableProvider } from "./audience-meta";
import type { ProviderCreate, ProviderUpdate } from "./use-audiences-data";

export const ALGORITHMS = ["RS256", "ES256", "HS256"] as const;
export type Algorithm = (typeof ALGORITHMS)[number];

export interface ClaimRow {
  claim: string;
  value: string;
  code: string;
}

export interface GroupRow {
  group: string;
  code: string;
}

/** Flat form state shared by every provider type (only the type's fields are sent). */
export interface ProviderValues {
  name: string;
  algorithm: Algorithm;
  issuer: string;
  audience: string;
  jwksUrl: string;
  sharedSecret: string;
  domain: string;
  clientId: string;
  clientSecret: string;
  entraTenantId: string;
  audiences: string[];
  claims: ClaimRow[];
  groups: GroupRow[];
}

const JWT_TYPES: ReadonlySet<CreatableProvider> = new Set(["portal_sso", "widget_jwt"]);
const GROUP_TYPES: ReadonlySet<CreatableProvider> = new Set(["google_workspace", "microsoft_entra"]);

export const usesJwt = (type: CreatableProvider) => JWT_TYPES.has(type);
export const usesGroups = (type: CreatableProvider) => GROUP_TYPES.has(type);

function isAlgorithm(value: string | null): value is Algorithm {
  return ALGORITHMS.some((a) => a === value);
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function codeOf(row: Record<string, unknown>): string {
  const audiences = row.audiences;
  return Array.isArray(audiences) && typeof audiences[0] === "number" ? String(audiences[0]) : "";
}

export function valuesFrom(provider: IdentityProviderOut | null, defaultName: string): ProviderValues {
  return {
    name: provider?.name ?? defaultName,
    algorithm: provider && isAlgorithm(provider.algorithm) ? provider.algorithm : "RS256",
    issuer: provider?.issuer ?? "",
    audience: provider?.audience ?? "",
    jwksUrl: provider?.jwks_url ?? "",
    sharedSecret: "",
    domain: provider?.domain ?? "",
    clientId: provider?.client_id ?? "",
    clientSecret: "",
    entraTenantId: provider?.entra_tenant_id ?? "",
    audiences: (provider?.audiences ?? []).map(String),
    claims: (provider?.claim_mappings ?? []).map((row) => ({ claim: str(row.claim), value: str(row.value), code: codeOf(row) })),
    groups: (provider?.group_mappings ?? []).map((row) => ({ group: str(row.group), code: codeOf(row) })),
  };
}

const orNull = (value: string) => (value.trim() ? value.trim() : null);

/** Client mirror of the backend's required-field rules (`providers._validate`); returns a reason key. */
export function missingRequired(type: CreatableProvider, values: ProviderValues, hasSecret: boolean): string | null {
  if (type === "google_workspace" && !values.domain.trim()) return "requiredDomain";
  if (type === "microsoft_entra" && !(values.entraTenantId.trim() && values.clientId.trim())) return "requiredEntra";
  if (type === "portal_sso" && !(values.issuer.trim() && (values.jwksUrl.trim() || values.sharedSecret.trim() || hasSecret))) return "requiredPortal";
  return null;
}

function typeFields(type: CreatableProvider, values: ProviderValues): ProviderUpdate {
  const claimMappings = values.claims
    .filter((r) => r.claim.trim() && r.value.trim() && r.code)
    .map((r) => ({ claim: r.claim.trim(), value: r.value.trim(), audiences: [Number(r.code)] }));
  const groupMappings = values.groups.filter((r) => r.group.trim() && r.code).map((r) => ({ group: r.group.trim(), audiences: [Number(r.code)] }));
  if (usesJwt(type)) {
    const secret = values.sharedSecret.trim();
    return {
      algorithm: values.algorithm,
      issuer: orNull(values.issuer),
      audience: orNull(values.audience),
      jwks_url: orNull(values.jwksUrl),
      claim_mappings: claimMappings,
      ...(secret ? { secrets: { shared_secret: secret } } : {}),
    };
  }
  if (type === "google_workspace") return { domain: orNull(values.domain), group_mappings: groupMappings };
  if (type === "microsoft_entra") {
    const secret = values.clientSecret.trim();
    return {
      entra_tenant_id: orNull(values.entraTenantId),
      client_id: orNull(values.clientId),
      group_mappings: groupMappings,
      ...(secret ? { secrets: { client_secret: secret } } : {}),
    };
  }
  return {};
}

/** Request bodies for create (POST) and update (PATCH); secrets are only sent when typed. */
export function providerBodies(type: CreatableProvider, values: ProviderValues): { create: ProviderCreate; update: ProviderUpdate } {
  const audiences = values.audiences.map(Number).filter((n) => Number.isInteger(n) && n > 0);
  const update: ProviderUpdate = { name: values.name.trim(), audiences: audiences.length > 0 ? audiences : null, ...typeFields(type, values) };
  return { update, create: { ...update, type, name: values.name.trim() } };
}
